import compact from "lodash/compact";
import map from "lodash/map";
import { SECONDARY_FRAMEWORKS_LIMIT } from "PFApp/constants/activity_form/skills_frameworks_limits";
import { getUnselectedSkillsFrameworksSuggestions } from "PFApp/helpers/activity_form/get_unselected_skills_frameworks_suggestions";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Icon } from "PFComponents/icon";
import { Select } from "PFComponents/select";
import SidePanel from "PFComponents/side_panel/side_panel";
import Toggle from "PFComponents/toggle/toggle";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import {
  fetchSkillsFrameworksOptions,
  SkillsFrameworksOptionsResponse
} from "PFCore/services/skills_frameworks/fetch_skills_frameworks_options";
import { Profile, SkillsFramework, SkillsFrameworkRank, Subtemplate, Template } from "PFCore/types";
import { FetchOptionsResponse, Option } from "PFTypes/options";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConflictsReviewSidePanel } from "./conflicts_review_side_panel";
import { useCanApplySkillsFramework } from "./hooks/use_can_apply_skills_framework";
import { useConflictResolver } from "./hooks/use_conflict_resolver";
import { usePopulateSkillsFrameworks } from "./hooks/use_populate_skills_frameworks";
import { AttributesBox } from "./parts/attributes_box";
import { SkillsFrameworksSuggestionsBox } from "./parts/skills_frameworks_suggestions_box/skills_frameworks_suggestions_box";

type SkillsFrameworksSidePanelProps = {
  activityCreator: Profile | null;
  closeOnClickOutside?: boolean;
  isConfigMatchFilterOn?: boolean;
  isSkillsFrameworksSuggestionsFetching?: boolean;
  onClose: VoidFunction;
  onFrameworksChange: (selectedFrameworks: SkillsFramework[]) => void;
  onPostPopulate?: (data: FormFieldData[]) => void;
  roleFormKeyPrefix?: string;
  initiallyPreselectedSkillsFramework?: SkillsFramework;
  selectedSkillsFrameworks: SkillsFramework[];
  setIsConfigMatchFilterOn?: Dispatch<SetStateAction<boolean>>;
  showConfigMatchFilterToggle?: boolean;
  showSkillsFrameworksSuggestions?: boolean;
  primarySkillsFrameworksSuggestions?: SkillsFramework[];
  secondarySkillsFrameworksSuggestions?: SkillsFramework[];
  subtemplate: Subtemplate | null;
  template: Template;
};

const formatOptions = (response: SkillsFrameworksOptionsResponse): FetchOptionsResponse<SkillsFramework> => ({
  ...response,
  entries: response.entries.map((skillsFramework) => ({
    id: skillsFramework.id,
    value: skillsFramework.name,
    displayElement: skillsFramework.name,
    original: skillsFramework
  }))
});

export const SkillsFrameworksSidePanel = ({
  activityCreator,
  closeOnClickOutside,
  isConfigMatchFilterOn,
  isSkillsFrameworksSuggestionsFetching = false,
  onClose,
  onFrameworksChange,
  onPostPopulate,
  roleFormKeyPrefix,
  selectedSkillsFrameworks,
  initiallyPreselectedSkillsFramework,
  setIsConfigMatchFilterOn,
  showConfigMatchFilterToggle = false,
  showSkillsFrameworksSuggestions = false,
  primarySkillsFrameworksSuggestions = [],
  secondarySkillsFrameworksSuggestions = [],
  subtemplate,
  template
}: SkillsFrameworksSidePanelProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });
  const { t: tTranslation } = useTranslation();
  const { populateSkillsFrameworksValues } = usePopulateSkillsFrameworks({
    template,
    subtemplate,
    activityCreator,
    roleFormKeyPrefix,
    onPostPopulate
  });

  const canApplyChanges = useCanApplySkillsFramework({ activityCreator });

  const [primaryFramework, setPrimaryFramework] = useState<SkillsFramework | undefined>(
    initiallyPreselectedSkillsFramework?.rank === SkillsFrameworkRank.Primary
      ? initiallyPreselectedSkillsFramework
      : selectedSkillsFrameworks.find(({ rank }) => rank === SkillsFrameworkRank.Primary)
  );
  const [secondaryFrameworks, setSecondaryFrameworks] = useState<SkillsFramework[]>(() => {
    const selectedSecondaryFrameworks = selectedSkillsFrameworks.filter(
      ({ rank }) => rank === SkillsFrameworkRank.Secondary
    );

    return initiallyPreselectedSkillsFramework?.rank === SkillsFrameworkRank.Secondary
      ? [...selectedSecondaryFrameworks, initiallyPreselectedSkillsFramework]
      : selectedSecondaryFrameworks;
  });
  const [isConflictsSidePanelOpen, setConflictsSidePanelOpen] = useState(false);

  const selectedFrameworks = useMemo(
    () => compact([primaryFramework, ...secondaryFrameworks]),
    [primaryFramework, secondaryFrameworks]
  );

  const { conflicts, resolvedAttributes, handleConflictsResolve } = useConflictResolver({
    selectedFrameworks,
    template,
    subtemplate
  });

  const primaryFrameworksSuggestions = getUnselectedSkillsFrameworksSuggestions(
    primarySkillsFrameworksSuggestions,
    [primaryFramework]
  );
  const secondaryFrameworksSuggestions = getUnselectedSkillsFrameworksSuggestions(
    secondarySkillsFrameworksSuggestions,
    secondaryFrameworks
  );

  const isSelectedSecondaryFrameworksUnderLimit = secondaryFrameworks.length < SECONDARY_FRAMEWORKS_LIMIT;

  const handleSubmit = () => {
    onFrameworksChange(selectedFrameworks);
    populateSkillsFrameworksValues(resolvedAttributes.skills, resolvedAttributes.customFields);
  };

  const footerRenderer = ({ onSidePanelClose }) => (
    <Inline justifyContent="space-between">
      <Button kind="secondary" onClick={onSidePanelClose}>
        {tTranslation("cancel")}
      </Button>
      <Button
        onClick={() => {
          handleSubmit();
          onSidePanelClose();
        }}
        disabled={selectedFrameworks.length === 0 || !canApplyChanges(resolvedAttributes.customFields)}
      >
        {t("apply")}
      </Button>
    </Inline>
  );

  return (
    <SidePanel
      fullHeight
      show
      size="medium"
      closeOnClickOutside={closeOnClickOutside}
      title={t("addSkillsFrameworks")}
      footerRenderer={footerRenderer}
      onClose={onClose}
    >
      <Stack gap="spacingLg">
        {showConfigMatchFilterToggle && (
          <Inline alignItems="end">
            <Toggle
              compact
              checked={isConfigMatchFilterOn}
              description={t("sidePanel.configMatchFilter")}
              onChange={setIsConfigMatchFilterOn}
            />
            <Tooltip content={t("sidePanel.configMatchFilterTooltipContent")}>
              <Icon name="info" size="sm" />
            </Tooltip>
          </Inline>
        )}
        <Select<SkillsFramework>
          label={t("sidePanel.primarySkillsFramework")}
          fetchOptions={({ ids, searchTerm }) => {
            if (ids?.length) {
              return Promise.resolve(formatOptions({ entries: [primaryFramework!], meta: { total: 1 } }));
            }

            return fetchSkillsFrameworksOptions({
              rank: SkillsFrameworkRank.Primary,
              availableInDemand: true,
              ...(searchTerm ? { term: searchTerm } : {})
            }).then(formatOptions);
          }}
          value={primaryFramework?.id}
          onChange={({ original }: Option<SkillsFramework>) => setPrimaryFramework(original)}
          onClear={primaryFramework ? () => setPrimaryFramework(undefined) : undefined}
          isFetchingSelectedOptions={false}
        />
        {showSkillsFrameworksSuggestions && (
          <SkillsFrameworksSuggestionsBox
            header={t("sidePanel.primarySkillsFrameworkSuggestions")}
            isDisabled={!!primaryFramework}
            isFetching={isSkillsFrameworksSuggestionsFetching}
            suggestions={primaryFrameworksSuggestions}
            onSuggestionClick={setPrimaryFramework}
          />
        )}
        <Select<SkillsFramework>
          multiple
          limit={SECONDARY_FRAMEWORKS_LIMIT}
          label={t("sidePanel.secondarySkillsFramework")}
          fetchOptions={({ ids, searchTerm }) => {
            if (ids?.length) {
              return Promise.resolve(
                formatOptions({ entries: secondaryFrameworks, meta: { total: secondaryFrameworks.length } })
              );
            }

            return fetchSkillsFrameworksOptions({
              rank: SkillsFrameworkRank.Secondary,
              availableInDemand: true,
              ...(searchTerm ? { term: searchTerm } : {})
            }).then(formatOptions);
          }}
          value={map(secondaryFrameworks, "id")}
          onChange={(options: Option<SkillsFramework>[]) => setSecondaryFrameworks(map(options, "original"))}
          onClear={secondaryFrameworks.length ? () => setSecondaryFrameworks([]) : undefined}
          isFetchingSelectedOptions={false}
        />
        {showSkillsFrameworksSuggestions && (
          <SkillsFrameworksSuggestionsBox
            header={t("sidePanel.secondarySkillsFrameworkSuggestions")}
            isDisabled={!isSelectedSecondaryFrameworksUnderLimit}
            isFetching={isSkillsFrameworksSuggestionsFetching}
            suggestions={secondaryFrameworksSuggestions}
            onSuggestionClick={(skillsFramework) => {
              if (isSelectedSecondaryFrameworksUnderLimit) {
                setSecondaryFrameworks((prev) => [...prev, skillsFramework]);
              }
            }}
          />
        )}
        <Divider />
        {conflicts.length > 0 && (
          <Alert
            title={t("sidePanel.thereAreConflicts", { count: conflicts.length })}
            action={{
              text: t("sidePanel.review"),
              small: true,
              onClick: () => setConflictsSidePanelOpen(true)
            }}
          />
        )}
        <Typography variant="bodyBold">{t("sidePanel.attributesFromAllSkillsFrameworks")}</Typography>
        <AttributesBox
          skills={resolvedAttributes.skills}
          customFields={resolvedAttributes.customFields}
          template={template}
          subtemplate={subtemplate}
          activityCreator={activityCreator}
        />
      </Stack>
      {isConflictsSidePanelOpen && (
        <ConflictsReviewSidePanel
          conflicts={conflicts}
          onSave={handleConflictsResolve}
          onClose={() => setConflictsSidePanelOpen(false)}
        />
      )}
    </SidePanel>
  );
};
